import React, { useCallback, useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import createClasses from './styles';
import { useTranslation } from 'react-i18next';
import { deleteRequest } from '../../../utils/requests';
import {
  Checkbox,
  InputLabel,
  showNotif,
  useRightSideBar
} from '@p81-common/ui';

interface DeactivateTenantProps {
  companyName: string;
  tenantId: string;
  updateDataFn(): void;
}

const DeactivateTenant = ({
  companyName,
  tenantId,
  updateDataFn
}: DeactivateTenantProps) => {
  const { t } = useTranslation(['activity']);
  const classes = createClasses();
  const { setShowBottomActionBar, setOpenSecondary } = useRightSideBar();
  const [confirmationCheckbox, setConfirmationCheckbox] =
    useState<boolean>(false);

  const handleSubmit = useCallback(
    async e => {
      e.preventDefault();

      try {
        const response = await deleteRequest('customer/deactivate', {
          tenantId
        });

        if (response.statusCode >= 400) {
          throw new Error();
        }

        showNotif(t('activity:tenantDeactivation.SUCCESS', { companyName }), {
          alertProps: {
            severity: 'error',
            variant: 'filled',
            style: { width: 'max-content' }
          }
        });
        updateDataFn();
        setOpenSecondary(false);
      } catch (e) {
        showNotif(t('activity:tenantDeactivation.FAILED'), {
          alertProps: {
            severity: 'success',
            variant: 'filled',
            style: { width: 'max-content' }
          }
        });
      }
    },
    [companyName, setOpenSecondary, t, tenantId, updateDataFn]
  );

  const handleCheckbox = () => {
    setConfirmationCheckbox(prev => !prev);
  };

  useEffect(() => {
    setShowBottomActionBar({
      okButtonProps: {
        text: t('buttons.APPLY'),
        disabled: !confirmationCheckbox,
        type: 'submit',
        onClick: e => handleSubmit(e)
      },
      cancelButtonProps: {
        text: t('buttons.CANCEL'),
        onClick: () => {
          setOpenSecondary(false);
          setShowBottomActionBar(null);
        }
      },
      isSideBar: true
    });
  }, [
    confirmationCheckbox,
    handleSubmit,
    setOpenSecondary,
    setShowBottomActionBar,
    t
  ]);

  return (
    <form className={classes.container} onSubmit={handleSubmit}>
      <Typography
        variant="h2"
        className={classes.heading}
        dangerouslySetInnerHTML={{
          __html: t('tenantDeactivation.TITLE', { companyName })
        }}
      />
      <Typography variant="subtitle1" className={classes.subtitle}>
        {t('tenantDeactivation.SUBTITLE')}
      </Typography>

      <InputLabel className={classes.checkbox}>
        <Checkbox value={confirmationCheckbox} onChange={handleCheckbox} />
        <Typography variant="subtitle2">
          {t('tenantDeactivation.CHECKBOX_LABEL')}
        </Typography>
      </InputLabel>
    </form>
  );
};

export default DeactivateTenant;
